import { useMemo } from 'react'
import useSmartAccount from 'src/common/api-clients/safe-protocol-kit/useSmartAccount'
import useInvestAssets from 'src/common/apollo/hooks/useInvestAssets'
import useOwnedCarbonTokens from 'src/common/apollo/hooks/useOwnedCarbonTokens'
import useOwnedGoldTokens from 'src/common/apollo/hooks/useOwnedGoldTokens'
import { useAllPriceFeeds } from 'src/common/queries/usePriceFeeds/usePriceFeeds'
import { getPriceFromPriceFeed } from 'src/common/queries/usePriceFeeds/utils'
import { useAllTokensForOwner } from 'src/common/queries/useTokensForOwner/useAllTokensForOwner'
import { big, ZERO } from 'src/common/utils/big-utils'
import { mapBy } from 'src/common/utils/collections/mapBy'

const useAvailableBalance = () => {
  const { smartAccountInfo } = useSmartAccount()
  const { priceFeeds, isLoading: priceFeedsLoading } = useAllPriceFeeds()

  const { ownedGoldTokens, loading: ownedGoldTokensLoading } =
    useOwnedGoldTokens(smartAccountInfo?.address)

  const { ownedErc20s, isLoading: ownedErc20sLoading } = useAllTokensForOwner(
    smartAccountInfo?.address || ''
  )

  const {
    ownedCarbonTokens: ownedCardbonNft,
    total,
    loading: carbonNftLoading,
  } = useOwnedCarbonTokens(smartAccountInfo?.address || '')

  const ownedErc20sDictionary = mapBy(ownedErc20s, 'id')

  const { investAssets, loading: investAssetsLoading } = useInvestAssets()

  const goldBalanceInUsd = useMemo(() => {
    return ownedGoldTokens.reduce((sum, token) => {
      const exchangePrice = getPriceFromPriceFeed(
        priceFeeds,
        token.contractAddress
      )
      if (exchangePrice) {
        const balanceInUsd = big(token.balance).mul(exchangePrice)
        return sum.plus(balanceInUsd)
      }
      return sum
    }, ZERO)
  }, [ownedGoldTokens, priceFeeds])

  const carbonBalanceInUsd = useMemo(() => {
    if (!ownedCardbonNft || !ownedCardbonNft[0]) return ZERO
    const exchangePrice = getPriceFromPriceFeed(
      priceFeeds,
      ownedCardbonNft[0].contractAddress
    )
    if (exchangePrice) {
      const balanceInUsd = big(total).mul(exchangePrice)
      return balanceInUsd
    } else {
      return ZERO
    }
  }, [ownedCardbonNft, priceFeeds, total])

  const rwaBalanceInUsd = useMemo(() => {
    return investAssets.reduce((sum, token) => {
      const exchangePrice = getPriceFromPriceFeed(
        priceFeeds,
        token.contractAddress
      )
      const tokenWithBalance = ownedErc20sDictionary.get(token.id)
      if (exchangePrice && tokenWithBalance) {
        const balanceInUsd = big(tokenWithBalance.balance).mul(exchangePrice)
        return sum.plus(balanceInUsd)
      }
      return sum
    }, ZERO)
  }, [investAssets, ownedErc20sDictionary, priceFeeds])

  const cryptoBalanceInUsd = useMemo(() => {
    const sum = ownedErc20s.reduce((sum, token) => {
      const exchangePrice = getPriceFromPriceFeed(
        priceFeeds,
        token.contractAddress
      )
      if (exchangePrice) {
        const balanceInUsd = big(token.balance).mul(exchangePrice)
        return sum.plus(balanceInUsd)
      }
      return sum
    }, ZERO)

    return sum.minus(rwaBalanceInUsd)
  }, [ownedErc20s, priceFeeds, rwaBalanceInUsd])

  const totalBalanceInUsd = goldBalanceInUsd
    .plus(rwaBalanceInUsd)
    .plus(cryptoBalanceInUsd)
    .plus(carbonBalanceInUsd)

  return {
    goldBalanceInUsd,
    rwaBalanceInUsd,
    cryptoBalanceInUsd,
    totalBalanceInUsd,
    carbonBalanceInUsd,
    loading:
      ownedGoldTokensLoading ||
      ownedErc20sLoading ||
      priceFeedsLoading ||
      investAssetsLoading ||
      carbonNftLoading,
  }
}

export default useAvailableBalance
